/**
 * AiCreditBadge + useAiCreditAction — always surface AI credit usage/consumption
 * whenever an AI action runs in the cabinet (AdminClient) portal.
 * Mirrors the frontend-client kit, wired to THIS portal's credits.service.
 * ---------------------------------------------------------------------------
 *  - <AiCreditBadge>      : a compact live chip showing the remaining AI-credit
 *                           balance, plus (optionally) the estimated cost of the
 *                           action about to run and/or what it just consumed.
 *  - useAiCreditAction()  : wrap any async AI call so the balance refreshes and a
 *                           "X crédits utilisés · solde N" toast fires afterwards.
 *
 * Project DoD: "Always display AI credit usage/consumption when AI used."
 *
 * Usage:
 *   const ai = useAiCreditAction({ feature: 'vendor.assess', defaultCost: 5 })
 *   <AiCreditBadge cost={5} consumed={ai.lastConsumed} />
 *   await ai.run(() => assess(body), { cost: 5 })
 */
import * as React from 'react'
import { BrainCircuit, Coins, Sparkles } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useCreditBalance, invalidateCredits } from '@/services/credits.service'
import { formatNumber } from '@/lib/jurisdiction/jurisdiction-config'

/** Rough model class → drives the free (OSS) vs metered (commercial) hint. */
export type AiModelClass = 'oss' | 'commercial'

function inferModelClass(model?: string | null): AiModelClass | undefined {
  if (!model) return undefined
  const m = model.toLowerCase()
  if (m.includes('llama') || m.includes('qwen') || m.includes('mistral') || m.includes('oss') || m.includes('local')) {
    return 'oss'
  }
  if (m.includes('gpt') || m.includes('claude') || m.includes('gemini') || m.includes('sonnet') || m.includes('opus')) {
    return 'commercial'
  }
  return undefined
}

// ── AiActionMeta (inline meta chips: cost / consumed / model) ─────────────────
interface AiActionMetaProps {
  estimatedCredits?: number
  consumedCredits?: number
  model?: string | null
  modelClass?: AiModelClass
  size?: 'sm' | 'md'
}

function AiActionMeta({ estimatedCredits, consumedCredits, model, modelClass, size = 'sm' }: AiActionMetaProps) {
  const text = size === 'sm' ? 'text-[10px]' : 'text-xs'
  const cls = modelClass ?? inferModelClass(model)
  const chip = 'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium'
  return (
    <span className="inline-flex items-center gap-1.5">
      {typeof estimatedCredits === 'number' && (
        <span className={cn(chip, text, 'border-amber-500/25 bg-amber-500/10 text-amber-600')} title="Coût estimé">
          <Coins className="h-3 w-3" />~{estimatedCredits}
        </span>
      )}
      {typeof consumedCredits === 'number' && (
        <span className={cn(chip, text, 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600')} title="Crédits consommés">
          <Sparkles className="h-3 w-3" />{consumedCredits} utilisé{consumedCredits > 1 ? 's' : ''}
        </span>
      )}
      {cls && (
        <span
          className={cn(
            chip,
            text,
            cls === 'oss'
              ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600'
              : 'border-primary/25 bg-primary/10 text-primary',
          )}
          title={cls === 'oss' ? 'Modèle open-source (gratuit)' : 'Modèle commercial (facturé)'}
        >
          {cls === 'oss' ? 'OSS · gratuit' : 'Commercial'}
        </span>
      )}
    </span>
  )
}

// ── AiCreditBadge ─────────────────────────────────────────────────────────────
export interface AiCreditBadgeProps {
  /** Estimated cost of the action about to run (credits). */
  cost?: number | null
  /** Credits actually consumed once the call settles. */
  consumed?: number | null
  /** Raw model id — drives the OSS/commercial sub-badge (free vs metered). */
  model?: string | null
  modelClass?: AiModelClass
  /** Show the live remaining-balance pill. Default true. */
  showBalance?: boolean
  size?: 'sm' | 'md'
  className?: string
}

/** Live "solde" pill + (optional) model/cost meta. */
export function AiCreditBadge({
  cost,
  consumed,
  model,
  modelClass,
  showBalance = true,
  size = 'sm',
  className,
}: AiCreditBadgeProps) {
  const { data } = useCreditBalance()
  const remaining = data?.ai?.total ?? 0
  const text = size === 'sm' ? 'text-[10px]' : 'text-xs'

  return (
    <div className={cn('inline-flex items-center gap-1.5', className)} data-testid="ai-credit-badge">
      {showBalance && (
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 font-semibold text-primary',
            text,
          )}
          title="Crédits IA disponibles"
        >
          <BrainCircuit className="h-3 w-3" />
          <span className="tabular-nums">{formatNumber(remaining)}</span>
          <span className="font-normal opacity-70">crédits</span>
        </span>
      )}
      {(typeof cost === 'number' || typeof consumed === 'number' || model || modelClass) && (
        <AiActionMeta
          estimatedCredits={cost ?? undefined}
          consumedCredits={consumed ?? undefined}
          model={model}
          modelClass={modelClass}
          size={size}
        />
      )}
    </div>
  )
}

export default AiCreditBadge

// ── useAiCreditAction ─────────────────────────────────────────────────────────
export interface RunAiOptions {
  /** Credits this specific call is expected to consume. */
  cost?: number
  /** Human label for the feature (used in toasts). */
  label?: string
  /** Announce the estimate before running. Default true when cost>0. */
  announce?: boolean
  /** Success toast text. Default "Action IA terminée". */
  successMessage?: string
}

export interface UseAiCreditActionOptions {
  feature?: string
  defaultCost?: number
}

export interface UseAiCreditAction {
  /** Live remaining AI-credit balance. */
  remaining: number
  /** True while a wrapped call is in flight. */
  isRunning: boolean
  /** Credits reported consumed by the last run (falls back to the estimate). */
  lastConsumed: number | null
  /**
   * Run an async AI call: announces the estimate, runs it, refreshes the credit
   * balance, then toasts the consumption + new balance. Returns the call result
   * (or throws — callers can try/catch, or omit and rely on the error toast).
   */
  run: <T>(fn: () => Promise<T>, opts?: RunAiOptions) => Promise<T>
}

export function useAiCreditAction(options: UseAiCreditActionOptions = {}): UseAiCreditAction {
  const qc = useQueryClient()
  const { data } = useCreditBalance()
  const [isRunning, setIsRunning] = React.useState(false)
  const [lastConsumed, setLastConsumed] = React.useState<number | null>(null)
  const remaining = data?.ai?.total ?? 0

  const run = React.useCallback(
    async <T,>(fn: () => Promise<T>, opts: RunAiOptions = {}): Promise<T> => {
      const cost = opts.cost ?? options.defaultCost ?? 0
      const label = opts.label || 'Action IA'
      const announce = opts.announce ?? cost > 0
      if (announce) {
        toast.message(`${label} en cours…`, {
          description: `~${cost} crédit${cost > 1 ? 's' : ''} • solde ${formatNumber(remaining)}`,
        })
      }
      setIsRunning(true)
      try {
        const result = await fn()
        // Refresh the balance everywhere (top-bar chip, badges, credit pages).
        invalidateCredits(qc)
        const consumed = cost
        setLastConsumed(consumed)
        const newBalance = Math.max(0, remaining - consumed)
        toast.success(opts.successMessage || `${label} terminée`, {
          description:
            cost > 0
              ? `${consumed} crédit${consumed > 1 ? 's' : ''} utilisé${consumed > 1 ? 's' : ''} • solde ${formatNumber(newBalance)}`
              : 'Aucun crédit consommé (modèle gratuit)',
        })
        return result
      } finally {
        setIsRunning(false)
      }
    },
    [qc, options.defaultCost, remaining],
  )

  return { remaining, isRunning, lastConsumed, run }
}

/** Imperative one-off toast: "N crédits utilisés · solde M" after an AI call. */
export function notifyAiCreditConsumed(consumed: number, remaining?: number): void {
  toast.success('Crédits IA', {
    description:
      consumed > 0
        ? `${consumed} crédit${consumed > 1 ? 's' : ''} utilisé${consumed > 1 ? 's' : ''}${
            typeof remaining === 'number' ? ` • solde ${formatNumber(remaining)}` : ''
          }`
        : 'Aucun crédit consommé (modèle gratuit)',
    icon: <Coins className="h-4 w-4" />,
  })
}
